import {
  distanceKm,
  geohashCenter,
  type BoardPost,
  type LatLng,
  type PostDoc,
} from '@talkofthetown/shared';
import { doc, getDoc, type Timestamp } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { config } from '../config';
import { callables, db } from '../firebase';
import type { AuthState } from '../hooks/useAuth';
import { friendlyError } from '../lib/errors';
import { domainOf, formatDistance, timeAgo } from '../lib/format';
import { imageUrl } from '../lib/images';
import { recordOpen } from '../lib/opens';
import { Dialog } from './Dialog';
import { PromotePanel } from './PromotePanel';
import { useToast } from './Toasts';

type Post = PostDoc<Timestamp>;

interface Props {
  /** The post shown (it stays set while the dialog animates closed). */
  postId: string | null;
  open: boolean;
  summary: BoardPost | null;
  center: LatLng | null;
  now: number;
  auth: AuthState;
  onClose: () => void;
  /** The dialog has finished closing. */
  onClosed: () => void;
}

const REASONS = [
  'Spam or scam',
  'Hateful or abusive',
  'Sexual content',
  'Violence',
  'False or misleading',
  'Other',
];

export function PostDialog({ postId, open, summary, center, now, auth, onClose, onClosed }: Props) {
  const toast = useToast();
  const [post, setPost] = useState<Post | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'gone'>('loading');
  const [imageIndex, setImageIndex] = useState(0);
  const [reporting, setReporting] = useState(false);
  const [busy, setBusy] = useState(false);

  // The parent remounts this dialog per post (key={postId}), so state starts fresh for each one.
  // Live posts are public; authors (and admins) can also see posts still pending or held.
  const uid = auth.user?.uid;
  useEffect(() => {
    if (!postId) return;
    let cancelled = false;
    getDoc(doc(db, 'posts', postId))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.data() as Post | undefined;
        const visible =
          data && (data.status === 'live' || auth.admin || (uid && data.authorId === uid));
        if (visible) {
          setPost(data);
          setState('ready');
        } else setState('gone');
      })
      .catch(() => !cancelled && setState('gone'));
    return () => {
      cancelled = true;
    };
  }, [postId, uid, auth.admin]);

  const mine = !!post && !!uid && post.authorId === uid;

  // Opening a live post is what gives it engagement points (once per viewer per day), and what
  // turns a whisper into a voice. An author opening their own post doesn't count.
  const live = post?.status === 'live';
  useEffect(() => {
    if (postId && live && !mine) void recordOpen(postId, auth.user);
  }, [postId, live, uid, mine]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = post?.title ?? summary?.title ?? '';
  const createdAt = post ? post.createdAt.toMillis() : summary?.createdAt;
  const g5 = post?.g5 ?? summary?.g5 ?? null;
  const distance = formatDistance(g5 && center ? distanceKm(center, geohashCenter(g5)) : null);
  const isAgent = post ? post.authorKind === 'agent' : !!summary?.isAgent;

  async function share() {
    // /p/<id> serves a rich preview to chat apps, then opens the post.
    const url = `${location.origin}/p/${postId}`;
    try {
      if (navigator.share) await navigator.share({ title, url });
      else {
        await navigator.clipboard.writeText(url);
        toast('Link copied');
      }
    } catch {
      /* share sheet dismissed */
    }
  }

  async function report(reason: string) {
    if (!postId) return;
    setBusy(true);
    try {
      const res = await callables.reportPost({ postId, reason });
      toast(
        res.data.hidden
          ? 'Thanks. The post has been hidden for review.'
          : 'Thanks for reporting. We’ll take a look.',
      );
      setReporting(false);
      if (res.data.hidden) onClose();
    } catch (err) {
      toast(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!postId || !window.confirm('Delete this post? It disappears from every board.')) return;
    setBusy(true);
    try {
      await callables.deletePost({ postId });
      toast('Post deleted');
      onClose();
    } catch (err) {
      toast(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function admin(action: 'hide' | 'ban' | 'restore') {
    if (!postId) return;
    const question = {
      hide: 'Hide this post for everyone?',
      ban: 'Ban this author and hide all their posts?',
      restore: 'Put this post back on the boards?',
    }[action];
    if (!window.confirm(question)) return;
    setBusy(true);
    try {
      await callables.adminAction({ action, postId });
      toast({ hide: 'Post hidden', ban: 'Author banned', restore: 'Post restored' }[action]);
      onClose();
    } catch (err) {
      toast(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  const images = post?.images ?? [];
  const current = images[Math.min(imageIndex, images.length - 1)];
  // The post's bubble on the board (a voice or a whisper), if it has one: the post opens out of it
  // and closes back into it.
  const tile = useCallback(
    () => (postId ? document.querySelector(`.board [data-post-id="${CSS.escape(postId)}"]`) : null),
    [postId],
  );

  return (
    <Dialog
      open={open && !!postId}
      onClose={onClose}
      onClosed={onClosed}
      origin={tile}
      labelledBy="post-title"
      className="post"
    >
      {state === 'gone' ? (
        <div className="post__gone">
          <h2 id="post-title">This post is no longer available</h2>
          <p>It may have been removed or is waiting for review.</p>
        </div>
      ) : (
        <article>
          {current && (
            <figure className="post__media">
              <img src={imageUrl(current.full)} alt="" width={current.w} height={current.h} />
              {images.length > 1 && (
                <div className="post__thumbs" role="group" aria-label="Images">
                  {images.map((img, i) => (
                    <button
                      key={img.thumb}
                      type="button"
                      aria-label={`Image ${i + 1} of ${images.length}`}
                      aria-pressed={i === imageIndex}
                      onClick={() => setImageIndex(i)}
                    >
                      <img src={imageUrl(img.thumb)} alt="" />
                    </button>
                  ))}
                </div>
              )}
            </figure>
          )}
          <div className="post__content">
            <h2 id="post-title" className="post__title">
              {title}
            </h2>
            <p className="post__byline">
              {!isAgent && (post?.authorName ?? summary?.author) && (
                <span className="avatar avatar--blank" aria-hidden="true">
                  {[...(post?.authorName ?? summary?.author ?? '')][0]!.toUpperCase()}
                </span>
              )}
              <span>{post?.authorName ?? summary?.author}</span>
              {isAgent && <span className="badge badge--ai">✦ AI summary</span>}
              {summary?.sponsored && <span className="badge badge--sponsored">Sponsored</span>}
              {createdAt !== undefined && (
                <span className="muted">· {timeAgo(createdAt, now)}</span>
              )}
              {distance && <span className="muted">· {distance}</span>}
            </p>
            {state === 'loading' && <p className="muted">Loading…</p>}
            {post && post.status !== 'live' && (
              <p className="post__status" role="status">
                {post.status === 'pending'
                  ? 'Checking your post… it’s whispered on the board within a few seconds.'
                  : post.status === 'held'
                    ? 'This post is held for review and only visible to its author and admins.'
                    : 'This post was removed.'}
              </p>
            )}
            {post?.body && <p className="post__body">{post.body}</p>}
            {post && post.links.length > 0 && (
              <ul className="post__links">
                {post.links.map((l) => (
                  <li key={l}>
                    <a href={l} target="_blank" rel="nofollow ugc noopener noreferrer">
                      <span className="post__link-domain">{domainOf(l)}</span>
                      <span className="post__link-url">{l}</span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
            <div className="post__actions">
              <button type="button" className="btn" onClick={share}>
                Share
              </button>
              {mine && post?.status !== 'removed' && (
                <button type="button" className="btn btn--ghost" disabled={busy} onClick={remove}>
                  Delete
                </button>
              )}
              {mine ? null : auth.signedIn ? (
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setReporting((r) => !r)}
                  aria-expanded={reporting}
                >
                  Report
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => auth.signInWithGoogle().catch((e) => toast(friendlyError(e)))}
                >
                  Sign in to report
                </button>
              )}
              {auth.admin && (
                <>
                  {live ? (
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={busy}
                      onClick={() => admin('hide')}
                    >
                      Hide post
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      onClick={() => admin('restore')}
                    >
                      Restore post
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn--danger"
                    disabled={busy}
                    onClick={() => admin('ban')}
                  >
                    Ban author
                  </button>
                </>
              )}
            </div>
            {mine && live && uid && config.payments && postId && (
              <PromotePanel postId={postId} uid={uid} onLeave={onClose} />
            )}
            {reporting && (
              <div className="post__report" role="group" aria-label="Why are you reporting this?">
                <p>Why are you reporting this?</p>
                <div className="chips">
                  {REASONS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className="chip"
                      disabled={busy}
                      onClick={() => report(r)}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      )}
    </Dialog>
  );
}
